import React, { useEffect, useRef, useState } from 'react'

import { BaseView } from '@/components/base'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useGetInterlaceWidgetToken } from '@/domain/wallet'
import { useUserStore } from '@/store/useUserStore'
import { responsiveAdapter } from '@/utils'
import config from '@/config/env'

interface WidgetInterlaceCardProps {
  kemCardId: string
  kemCardEnabled?: boolean
  onError?: () => void
}

export const WidgetInterlaceCard: React.FC<WidgetInterlaceCardProps> = ({
  kemCardId,
  kemCardEnabled = true,
  onError,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [iframeError, setIframeError] = useState(false)

  const userAuthenticated = useUserStore((s) => s.userAuthenticated)
  const cardholderName = userAuthenticated
    ? `${userAuthenticated.first_name || ''} ${userAuthenticated.last_name || ''}`.trim()
    : ''

  const { data: tokenData, isLoading: isTokenLoading, isError: isTokenError } =
    useGetInterlaceWidgetToken({ card_id: kemCardId })

  const widgetUrl = config.INTERLACE_WIDGET_URL

  // If token fetch fails, notify parent to fall back to static card
  useEffect(() => {
    if (isTokenError) {
      onError?.()
    }
  }, [isTokenError, onError])

  useEffect(() => {
    if (!isLoaded || !tokenData?.token || !iframeRef.current?.contentWindow) return

    const data = JSON.stringify({
      clientAccessToken: tokenData.token,
      cardholderName,
    })

    setTimeout(() => {
      iframeRef.current?.contentWindow?.postMessage(data, '*')
    }, 1000)
  }, [isLoaded, tokenData?.token, cardholderName])

  // No widget URL or token error — signal fallback
  if (!widgetUrl || isTokenError || iframeError) {
    return null
  }

  if (isTokenLoading) {
    return (
      <BaseView
        center
        style={{
          height: `${responsiveAdapter(202)}px`,
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        <LoadingSpinner />
      </BaseView>
    )
  }

  return (
    <BaseView
      style={{
        position: 'relative',
        height: `${responsiveAdapter(202)}px`,
        borderRadius: '20px',
        overflow: 'hidden',
      }}
    >
      <iframe
        ref={iframeRef}
        src={widgetUrl}
        onLoad={() => setIsLoaded(true)}
        onError={() => {
          setIframeError(true)
          onError?.()
        }}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: '20px',
        }}
        title="Interlace Card Widget"
        sandbox="allow-scripts allow-same-origin"
      />

      {!kemCardEnabled && (
        <BaseView
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '20px',
          }}
        />
      )}
    </BaseView>
  )
}
